import type { DocPageContent } from '../docs-content'

const page = {
  order: 2,
  slug: 'workspaces',
  title: '工作区与存储',
  description: '工作区文件夹如何运作、文件存放在哪里、权限管理以及缓存维护。',
  category: '入门',
  related: ['concepts', 'projects', 'troubleshooting'],
  sections: [
    {
      title: '工作区模型',
      blocks: [
        {
          kind: 'list',
          items: [
            '**工作区**是精剪读取和写入磁盘上的一个文件夹。',
            '精剪生成的所有内容都存放在那里：项目文件、媒体元数据、缩略图、波形、转录稿、场景数据、AI 素材、缓存以及导出文件。',
            '导入的源媒体不会被复制到工作区中——精剪会记录每个文件在磁盘上现有位置的链接。',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '由于工作区是一个真实的文件夹，你可以像备份、同步或移动任何其他目录一样对待它。',
        },
      ],
    },
    {
      title: '选择并切换工作区',
      blocks: [
        {
          kind: 'list',
          items: [
            '首次启动时，精剪会在编辑器打开前要求你选择一个工作区。',
            '选择一个你拥有权限的普通文件夹——例如“文档”中的某个文件夹。避免受保护的系统位置。',
            '使用**工作区**控件来添加、切换或移除已知的工作区。',
            '切换只会改变精剪所使用的文件夹，不会移动你的源媒体。',
          ],
        },
      ],
    },
    {
      title: '权限与重新连接',
      blocks: [
        {
          kind: 'paragraph',
          text: '出于安全考虑，浏览器可能会在会话之间使文件夹权限过期。',
        },
        {
          kind: 'list',
          items: [
            '当精剪显示**重新连接你的工作区**时，选择**重新连接**并授予对同一文件夹的读写权限。',
            '如果访问被拒绝，请重新选择该文件夹，并在浏览器提示时允许访问。',
            '如果该文件夹被移动、重命名或删除，请改为选择另一个文件夹。',
          ],
        },
      ],
    },
    {
      title: '缺失的媒体',
      blocks: [
        {
          kind: 'list',
          items: [
            '“缺失媒体”表示精剪当前无法读取一个或多个已链接的源文件。',
            '当某个文件仅需更新浏览器权限时，使用**授予访问权限**。',
            '当文件或其文件夹被移动时，使用**定位**或**定位文件夹**。',
            '使用**浏览其他文件夹**将精剪指向该媒体的新副本。',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '仅在计划稍后重新链接文件时才使用**离线工作**；在此之前，片段会一直保持为损坏的引用。',
        },
      ],
    },
    {
      title: '移动项目与清理缓存',
      blocks: [
        {
          kind: 'list',
          items: [
            '使用**导出项目**将某个项目及其媒体打包成一个可移植的包，供其他工作区导入。',
            '项目缓存操作会清除重新生成的数据，例如波形、胶片条、GIF 帧和解码后的音频。',
            '存储设置还允许你重新生成缩略图、生成缺失的代理文件，以及删除代理文件。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '清理缓存永远不会删除你的源媒体；当项目需要预览时，精剪会重新生成它们。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
